import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Target, Eye, Heart, Users, Award, Zap,
  ArrowRight, CheckCircle
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/SEO';

const About = () => {
  const { language } = useLanguage();

  const texts = {
    tr: {
      title: 'Hakkımızda',
      subtitle: 'Teknoloji ve yaratıcılığın buluştuğu nokta',
      story: {
        title: 'Hikayemiz',
        content: `Cplus Media LLC, dijital dönüşüm yolculuğuna çıkan şirketlere rehberlik etmek amacıyla kuruldu. Başlangıçtan bu yana, teknoloji ve yaratıcılığı birleştirerek müşterilerimize değer katmayı hedefledik.

Bugün, web geliştirmeden yapay zekaya, e-ticaretten 3D baskıya kadar geniş bir yelpazede hizmet sunuyoruz. Her projede en yüksek kalite standartlarını uygulayarak, müşterilerimizin dijital varlıklarını güçlendiriyoruz.

Ekibimiz, farklı disiplinlerden gelen uzmanlardan oluşuyor: yazılım mühendisleri, UX tasarımcıları, dijital pazarlama uzmanları ve proje yöneticileri. Bu çeşitlilik, her projeye 360 derece bakış açısı getirmemizi sağlıyor.`
      },
      mission: {
        title: 'Misyonumuz',
        content: 'İşletmelerin dijital potansiyellerini en üst düzeye çıkarmak için yenilikçi ve sürdürülebilir çözümler sunmak.'
      },
      vision: {
        title: 'Vizyonumuz',
        content: 'Türkiye\'nin lider dijital dönüşüm ortağı olarak, global standartlarda hizmet sunmak ve sektörde öncü olmak.'
      },
      values: {
        title: 'Değerlerimiz',
        items: [
          { icon: Target, title: 'Müşteri Odaklılık', desc: 'Her kararımızda müşteri başarısını ön planda tutuyoruz.' },
          { icon: Zap, title: 'Yenilikçilik', desc: 'En güncel teknolojileri takip ediyor ve uyguluyoruz.' },
          { icon: Heart, title: 'Tutku', desc: 'İşimizi seviyoruz ve bu tutkuyu her projemize yansıtıyoruz.' },
          { icon: Users, title: 'İşbirliği', desc: 'Ekip çalışması ve açık iletişim ile başarıya ulaşıyoruz.' },
          { icon: Award, title: 'Kalite', desc: 'Her projede en yüksek standartları hedefliyoruz.' },
          { icon: CheckCircle, title: 'Güvenilirlik', desc: 'Söz verdiğimiz her şeyi zamanında teslim ediyoruz.' }
        ]
      },
      stats: [
        { value: '150+', label: 'Tamamlanan Proje' },
        { value: '50+', label: 'Mutlu Müşteri' },
        { value: '6', label: 'Yıllık Deneyim' },
        { value: '15+', label: 'Uzman Ekip' }
      ],
      cta: {
        title: 'Birlikte çalışalım',
        subtitle: 'Projenizi konuşmak için bizimle iletişime geçin.',
        button: 'İletişime Geç'
      }
    },
    en: {
      title: 'About Us',
      subtitle: 'Where technology meets creativity',
      story: {
        title: 'Our Story',
        content: `Cplus Media LLC was founded to guide companies embarking on their digital transformation journey. From the beginning, we aimed to add value to our customers by combining technology and creativity.

Today, we offer services across a wide range from web development to artificial intelligence, from e-commerce to 3D printing. By applying the highest quality standards in every project, we strengthen our customers' digital presence.

Our team consists of experts from different disciplines: software engineers, UX designers, digital marketing specialists, and project managers. This diversity allows us to bring a 360-degree perspective to every project.`
      },
      mission: {
        title: 'Our Mission',
        content: 'To provide innovative and sustainable solutions to maximize the digital potential of businesses.'
      },
      vision: {
        title: 'Our Vision',
        content: 'To be Turkey\'s leading digital transformation partner, providing global-standard services and pioneering the industry.'
      },
      values: {
        title: 'Our Values',
        items: [
          { icon: Target, title: 'Customer Focus', desc: 'We prioritize customer success in every decision we make.' },
          { icon: Zap, title: 'Innovation', desc: 'We follow and implement the latest technologies.' },
          { icon: Heart, title: 'Passion', desc: 'We love our work and reflect this passion in every project.' },
          { icon: Users, title: 'Collaboration', desc: 'We achieve success through teamwork and open communication.' },
          { icon: Award, title: 'Quality', desc: 'We aim for the highest standards in every project.' },
          { icon: CheckCircle, title: 'Reliability', desc: 'We deliver everything we promise on time.' }
        ]
      },
      stats: [
        { value: '150+', label: 'Completed Projects' },
        { value: '50+', label: 'Happy Clients' },
        { value: '6', label: 'Years Experience' },
        { value: '15+', label: 'Expert Team' }
      ],
      cta: {
        title: "Let's work together",
        subtitle: 'Contact us to discuss your project.',
        button: 'Get in Touch'
      }
    }
  };

  const t = texts[language] || texts.tr;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <SEO
        title={language === 'tr' ? 'Hakkımızda - Vizyon & Misyon' : 'About Us - Vision & Mission'}
        description={language === 'tr' ? 'Cplus Media LLC, reklam ve teknoloji dünyasında yenilikçi çözümler sunan bir ajanstır. Misyonumuz, vizyonumuz ve değerlerimiz hakkında bilgi edinin.' : 'Cplus Media LLC gives innovative solutions in advertising and technology world. Learn about our mission, vision and values.'}
        keywords="hakkımızda, reklam ajansı, about us, digital agency, misyon vizyon"
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 py-24 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6"
          >
            {t.title}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-xl text-white/90 max-w-3xl mx-auto"
          >
            {t.subtitle}
          </motion.p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16 -mt-24 relative z-10"
        >
          {t.stats.map((stat, idx) => (
            <div
              key={idx}
              className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6 text-center shadow-lg"
            >
              <p className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                {stat.value}
              </p>
              <p className="text-gray-600 dark:text-gray-400 text-sm">{stat.label}</p>
            </div>
          ))}
        </motion.div>

        {/* Story */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
            {t.story.title}
          </h2>
          <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8">
            <p className="text-gray-700 dark:text-gray-300 whitespace-pre-line leading-relaxed">
              {t.story.content}
            </p>
          </div>
        </motion.section>

        {/* Mission & Vision */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid md:grid-cols-2 gap-8 mb-16"
        >
          <div className="bg-gradient-to-br from-blue-500 to-cyan-500 rounded-2xl p-8 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
              <Target className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold mb-4">{t.mission.title}</h3>
            <p className="text-white/90">{t.mission.content}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl p-8 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-4">
              <Eye className="w-6 h-6" />
            </div>
            <h3 className="text-2xl font-bold mb-4">{t.vision.title}</h3>
            <p className="text-white/90">{t.vision.content}</p>
          </div>
        </motion.section>

        {/* Values */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center">
            {t.values.title}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.values.items.map((value, idx) => {
              const Icon = value.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-6"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{value.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{value.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t.cta.title}
            </h2>
            <p className="text-white/80 mb-8">
              {t.cta.subtitle}
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-white text-blue-600 rounded-xl font-bold hover:shadow-xl hover:-translate-y-1 transition-all"
            >
              {t.cta.button}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default About;
